/**
 * Phase 20 — NewQuoteDialog with 3 quote-basis modes:
 *   A. Regels (line items)         — detailed quote, e.g. 14 separate items
 *   B. Totaalvolume (default)      — quick m³-based quote
 *   C. Vaste prijs / service       — fixed-amount quote (e.g. 20ft FCL)
 *
 * Backwards compatibility: the previous single-shipment fallback is no longer
 * the default for NEW quotes — but existing quotes without a basis (legacy)
 * remain fully readable & PDF-able via the unchanged legacy rendering path
 * on the backend.
 */
import React, { useEffect, useMemo, useState } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { toast } from "sonner";
import { Plus, Boxes, Calculator, Package, AlertTriangle } from "lucide-react";
import QuoteLineItemsEditor, { EMPTY_LINE } from "./QuoteLineItemsEditor";
import NAWAddressBlock from "./NAWAddressBlock";

const SHIPMENT_TYPES = [
  { value: "luchtvracht", label: "Luchtvracht" },
  { value: "zeevracht_lcl", label: "Zeevracht LCL" },
  { value: "zeevracht_fcl_20", label: "Zeevracht FCL 20ft" },
  { value: "zeevracht_fcl_40", label: "Zeevracht FCL 40ft" },
  { value: "verhuizing", label: "Verhuiszending" },
  { value: "pallet", label: "Pallet" },
  { value: "container", label: "Container" },
  { value: "overig", label: "Overig" },
];

const MODE_INFO = {
  total_volume: { Icon: Calculator, label: "Totaalvolume", hint: "Snelle offerte op basis van totaal m³" },
  lines: { Icon: Boxes, label: "Regels / items", hint: "Gedetailleerde offerte met meerdere items" },
  fixed: { Icon: Package, label: "Vaste prijs", hint: "Container, service of forfaitaire offerte" },
};

const EMPTY_CUSTOMER = {
  first_name: "", last_name: "", company_name: "",
  email: "", phone: "", whatsapp: "",
  // Phase 20E — gestructureerde NAW
  street: "", house_number: "", house_number_addition: "",
  postal_code: "", city: "", country: "Nederland",
  destination_country: "Curaçao",
  shipment_type: "zeevracht_lcl",
  internal_note: "",
};

const EMPTY_TOTAL_VOLUME = {
  total_volume_cbm: "",
  price_per_cbm: "",
  colli_count: "",
  admin_fee: "", documentation_fee: "", pickup_fee: "", extra_handling: "", discount: "",
  vat_percent: 21,
};

const EMPTY_FIXED = {
  fixed_description: "",
  fixed_amount: "",
  admin_fee: "", extra_handling: "", discount: "",
  vat_percent: 21,
};

function n(v) { const x = Number(v); return Number.isFinite(x) ? x : 0; }

// BTW 0% bug fix: only fall back to 21 when field is truly empty/invalid,
// NEVER when user explicitly chose 0. `n(0) || 21 === 21` is the bug we hunt.
function vatOrDefault(v) {
  if (v === "" || v === null || v === undefined) return 21;
  const x = Number(v);
  return Number.isFinite(x) && x >= 0 ? x : 21;
}

function lineTotal(line) {
  const qty = n(line.quantity) || 1;
  const rate = Math.max(0, n(line.rate));
  const lt = (line.line_type || "cbm").toLowerCase();
  if (lt === "fixed") return Math.round(rate * 100) / 100;
  if (lt === "qty") return Math.round(qty * rate * 100) / 100;
  let cbm = n(line.cbm);
  if (!cbm) {
    const L = n(line.length_cm), W = n(line.width_cm), H = n(line.height_cm);
    if (L && W && H) cbm = Math.round(((L * W * H) / 1_000_000) * 10000) / 10000;
  }
  return Math.round(qty * cbm * rate * 100) / 100;
}

function computeTotals(mode, payload) {
  const pricing = payload.pricing || {};
  const extras = ["admin_fee", "documentation_fee", "pickup_fee", "extra_handling"]
    .reduce((s, k) => s + Math.max(0, n(pricing[k])), 0);
  const discount = Math.max(0, n(pricing.discount));
  let base = 0;
  if (mode === "lines") {
    base = (payload.line_items || []).reduce((s, l) => s + lineTotal(l), 0);
  } else if (mode === "total_volume") {
    base = Math.max(0, n(payload.total_volume_cbm)) * Math.max(0, n(pricing.price_per_cbm));
  } else if (mode === "fixed") {
    base = Math.max(0, n(payload.fixed_amount));
  }
  const sub = Math.max(0, Math.round((base + extras - discount) * 100) / 100);
  const vatPct = vatOrDefault(pricing.vat_percent);
  const vat = Math.round(sub * (vatPct / 100) * 100) / 100;
  const total = Math.round((sub + vat) * 100) / 100;
  return { sub, vat, total, base, extras, discount };
}

// ----------- Mode toggle (top-level for R4 stability) -----------
function ModeToggle({ mode, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="quote-mode-toggle">
      {Object.entries(MODE_INFO).map(([k, info]) => {
        const Icon = info.Icon;
        const active = mode === k;
        return (
          <button
            type="button"
            key={k}
            onClick={() => onChange(k)}
            className={`text-left rounded-none border-2 p-3 transition-colors ${
              active
                ? "border-[#0055FF] bg-[#0055FF]/5"
                : "border-gray-200 bg-white hover:border-gray-400"
            }`}
            data-testid={`quote-mode-${k}`}
          >
            <div className="flex items-center gap-2">
              <Icon className={`w-4 h-4 ${active ? "text-[#0055FF]" : "text-gray-500"}`} />
              <span className={`font-bold text-sm ${active ? "text-[#0055FF]" : "text-[#0A192F]"}`}>
                {info.label}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-1">{info.hint}</div>
          </button>
        );
      })}
    </div>
  );
}

// ----------- Customer fields (top-level) -----------
function CustomerFields({ form, setF }) {
  const naw = {
    street: form.street, house_number: form.house_number,
    house_number_addition: form.house_number_addition,
    postal_code: form.postal_code, city: form.city, country: form.country,
  };
  const onAddressChange = (patch) => {
    Object.entries(patch).forEach(([k, v]) => setF(k, v));
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div><Label className="text-[10px] uppercase">Voornaam *</Label><Input data-testid="nq-first-name" required value={form.first_name} onChange={(e) => setF("first_name", e.target.value)} className="rounded-none mt-1" /></div>
        <div><Label className="text-[10px] uppercase">Achternaam *</Label><Input data-testid="nq-last-name" required value={form.last_name} onChange={(e) => setF("last_name", e.target.value)} className="rounded-none mt-1" /></div>
        <div><Label className="text-[10px] uppercase">Bedrijfsnaam</Label><Input data-testid="nq-company" value={form.company_name} onChange={(e) => setF("company_name", e.target.value)} className="rounded-none mt-1" /></div>
        <div><Label className="text-[10px] uppercase">E-mail *</Label><Input data-testid="nq-email" type="email" required value={form.email} onChange={(e) => setF("email", e.target.value)} className="rounded-none mt-1" /></div>
        <div><Label className="text-[10px] uppercase">Telefoon</Label><Input data-testid="nq-phone" value={form.phone} onChange={(e) => setF("phone", e.target.value)} className="rounded-none mt-1" /></div>
        <div><Label className="text-[10px] uppercase">WhatsApp</Label><Input data-testid="nq-whatsapp" value={form.whatsapp} onChange={(e) => setF("whatsapp", e.target.value)} className="rounded-none mt-1" /></div>
      </div>
      {/* Phase 20F — NAW met PDOK auto-lookup */}
      <NAWAddressBlock value={naw} onChange={onAddressChange} testidPrefix="nq" />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div><Label className="text-[10px] uppercase">Type zending</Label>
          <Select value={form.shipment_type} onValueChange={(v) => setF("shipment_type", v)}>
            <SelectTrigger data-testid="nq-shipment-type" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{SHIPMENT_TYPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2"><Label className="text-[10px] uppercase">Bestemming</Label><Input data-testid="nq-destination" value={form.destination_country} onChange={(e) => setF("destination_country", e.target.value)} className="rounded-none mt-1" /></div>
      </div>
    </div>
  );
}

// ----------- Totals panel (top-level) -----------
function TotalsPanel({ totals }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-xs">
      <div className="bg-gray-50 border border-gray-200 px-2 py-1.5">
        <div className="text-[10px] text-gray-500">Subtotaal excl. BTW</div>
        <div className="font-mono font-bold text-sm" data-testid="nq-derived-subtotal">€ {totals.sub.toFixed(2)}</div>
      </div>
      <div className="bg-gray-50 border border-gray-200 px-2 py-1.5">
        <div className="text-[10px] text-gray-500">BTW</div>
        <div className="font-mono font-bold text-sm" data-testid="nq-derived-vat">€ {totals.vat.toFixed(2)}</div>
      </div>
      <div className="bg-white border-2 border-[#0055FF] px-2 py-1.5">
        <div className="text-[10px] text-[#0055FF]">Totaal incl. BTW</div>
        <div className="font-mono font-bold text-sm text-[#0055FF]" data-testid="nq-derived-total">€ {totals.total.toFixed(2)}</div>
      </div>
    </div>
  );
}

// ----------- Pricing extras grid (top-level) -----------
function PricingExtrasGrid({ pricing, setP, showDocFee = true, showPickup = true }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      <div><Label className="text-[10px] uppercase">Administratie</Label><Input data-testid="nq-admin-fee" type="number" step="0.01" value={pricing.admin_fee} onChange={(e) => setP("admin_fee", e.target.value)} className="rounded-none mt-1" /></div>
      {showDocFee ? (
        <div><Label className="text-[10px] uppercase">Documentatie</Label><Input data-testid="nq-doc-fee" type="number" step="0.01" value={pricing.documentation_fee || ""} onChange={(e) => setP("documentation_fee", e.target.value)} className="rounded-none mt-1" /></div>
      ) : null}
      {showPickup ? (
        <div><Label className="text-[10px] uppercase">Pickup</Label><Input data-testid="nq-pickup-fee" type="number" step="0.01" value={pricing.pickup_fee || ""} onChange={(e) => setP("pickup_fee", e.target.value)} className="rounded-none mt-1" /></div>
      ) : null}
      <div><Label className="text-[10px] uppercase">Extra handling</Label><Input data-testid="nq-extra-handling" type="number" step="0.01" value={pricing.extra_handling || ""} onChange={(e) => setP("extra_handling", e.target.value)} className="rounded-none mt-1" /></div>
      <div><Label className="text-[10px] uppercase">Korting</Label><Input data-testid="nq-discount" type="number" step="0.01" value={pricing.discount || ""} onChange={(e) => setP("discount", e.target.value)} className="rounded-none mt-1" /></div>
      <div><Label className="text-[10px] uppercase">BTW %</Label><Input data-testid="nq-vat-pct" type="number" step="0.01" value={pricing.vat_percent} onChange={(e) => setP("vat_percent", e.target.value)} className="rounded-none mt-1" /></div>
    </div>
  );
}

// ----------- Total Volume mode (top-level) -----------
function TotalVolumeMode({ form, setF }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div>
          <Label className="text-[10px] uppercase">Totaal volume (m³) *</Label>
          <Input data-testid="nq-tv-cbm" type="number" min="0" step="0.01" value={form.total_volume_cbm}
            onChange={(e) => setF("total_volume_cbm", e.target.value)} className="rounded-none mt-1 font-mono" />
        </div>
        <div>
          <Label className="text-[10px] uppercase">Tarief per m³ (€) *</Label>
          <Input data-testid="nq-tv-rate" type="number" min="0" step="0.01" value={form.price_per_cbm}
            onChange={(e) => setF("price_per_cbm", e.target.value)} className="rounded-none mt-1 font-mono" />
        </div>
        <div>
          <Label className="text-[10px] uppercase">Aantal colli/items</Label>
          <Input data-testid="nq-tv-colli" type="number" min="0" value={form.colli_count}
            onChange={(e) => setF("colli_count", e.target.value)} className="rounded-none mt-1" />
        </div>
        <div className="bg-[#0055FF]/5 border border-[#0055FF]/30 px-2 py-1.5">
          <div className="text-[10px] text-[#0055FF] uppercase">Volume × tarief</div>
          <div className="font-mono font-bold text-[#0055FF]" data-testid="nq-tv-base">
            € {(Math.max(0, n(form.total_volume_cbm)) * Math.max(0, n(form.price_per_cbm))).toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ----------- Fixed mode (top-level) -----------
function FixedMode({ form, setF }) {
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-[10px] uppercase">Omschrijving service / vaste post *</Label>
        <Input data-testid="nq-fx-description" value={form.fixed_description}
          onChange={(e) => setF("fixed_description", e.target.value)}
          placeholder="Bv. Zeevracht 20ft container Rotterdam → Willemstad"
          className="rounded-none mt-1" />
      </div>
      <div>
        <Label className="text-[10px] uppercase">Bedrag (€) *</Label>
        <Input data-testid="nq-fx-amount" type="number" min="0" step="0.01" value={form.fixed_amount}
          onChange={(e) => setF("fixed_amount", e.target.value)} className="rounded-none mt-1 font-mono" />
      </div>
    </div>
  );
}

// ----------- Mode-switch warning (top-level) -----------
function ModeSwitchWarning({ show, onConfirm, onCancel }) {
  if (!show) return null;
  return (
    <div className="border-2 border-amber-400 bg-amber-50 p-3 flex items-start gap-2" data-testid="quote-mode-switch-warning">
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-sm">
        <p className="font-bold text-amber-900">Modus wisselen verwijdert ingevoerde gegevens.</p>
        <p className="text-amber-800 text-xs mt-1">U heeft regels of bedragen ingevuld. Doorgaan zal die wissen.</p>
        <div className="mt-2 flex gap-2">
          <Button type="button" size="sm" onClick={onConfirm} className="rounded-none bg-amber-600 hover:bg-amber-700 text-white h-7" data-testid="quote-mode-switch-confirm">
            Toch wisselen
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={onCancel} className="rounded-none h-7" data-testid="quote-mode-switch-cancel">
            Annuleren
          </Button>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// MAIN DIALOG
// =========================================================================
export default function NewQuoteDialog({ open, onClose, onCreated }) {
  const [mode, setMode] = useState("total_volume");
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [tv, setTv] = useState(EMPTY_TOTAL_VOLUME);
  const [fixed, setFixed] = useState(EMPTY_FIXED);
  const [lines, setLines] = useState([]);
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pendingMode, setPendingMode] = useState(null); // for switch warning

  // Load preset chips on first open
  useEffect(() => {
    if (!open) return;
    adminApi.get("/admin/quotes-v2/_meta/line-presets").then((r) => {
      setPresets(r.data?.presets || []);
    }).catch(() => setPresets([]));
  }, [open]);

  const setCustField = (k, v) => setCustomer((f) => ({ ...f, [k]: v }));
  const setTvField = (k, v) => setTv((f) => ({ ...f, [k]: v }));
  const setFixedField = (k, v) => setFixed((f) => ({ ...f, [k]: v }));

  const hasModeData = useMemo(() => {
    if (mode === "lines") return (lines || []).length > 0;
    if (mode === "total_volume") return n(tv.total_volume_cbm) > 0 || n(tv.price_per_cbm) > 0;
    if (mode === "fixed") return n(fixed.fixed_amount) > 0 || (fixed.fixed_description || "").trim().length > 0;
    return false;
  }, [mode, lines, tv, fixed]);

  const handleModeChange = (next) => {
    if (next === mode) return;
    if (hasModeData) {
      setPendingMode(next);
    } else {
      setMode(next);
    }
  };
  const confirmModeChange = () => {
    if (pendingMode) {
      // wipe other-mode data so we never POST stale fields
      if (pendingMode !== "lines") setLines([]);
      if (pendingMode !== "total_volume") setTv(EMPTY_TOTAL_VOLUME);
      if (pendingMode !== "fixed") setFixed(EMPTY_FIXED);
      setMode(pendingMode);
      setPendingMode(null);
    }
  };
  const cancelModeChange = () => setPendingMode(null);

  // Live totals preview
  const liveTotals = useMemo(() => {
    const pricing = mode === "total_volume"
      ? { price_per_cbm: tv.price_per_cbm, admin_fee: tv.admin_fee, documentation_fee: tv.documentation_fee, pickup_fee: tv.pickup_fee, extra_handling: tv.extra_handling, discount: tv.discount, vat_percent: tv.vat_percent }
      : mode === "fixed"
      ? { admin_fee: fixed.admin_fee, extra_handling: fixed.extra_handling, discount: fixed.discount, vat_percent: fixed.vat_percent }
      : { admin_fee: tv.admin_fee, documentation_fee: tv.documentation_fee, pickup_fee: tv.pickup_fee, extra_handling: tv.extra_handling, discount: tv.discount, vat_percent: tv.vat_percent };
    return computeTotals(mode, {
      line_items: lines,
      total_volume_cbm: tv.total_volume_cbm,
      fixed_amount: fixed.fixed_amount,
      pricing,
    });
  }, [mode, lines, tv, fixed]);

  const reset = () => {
    setMode("total_volume");
    setCustomer(EMPTY_CUSTOMER);
    setTv(EMPTY_TOTAL_VOLUME);
    setFixed(EMPTY_FIXED);
    setLines([]);
    setPendingMode(null);
  };

  const validate = () => {
    if (!customer.first_name || !customer.last_name) return "Vul voornaam en achternaam in.";
    if (!customer.email || !customer.email.includes("@")) return "Vul een geldig e-mailadres in.";
    if (mode === "lines") {
      const valid = (lines || []).filter((l) => (l.description || "").trim() || n(l.rate) > 0);
      if (valid.length === 0) return "Vul minimaal één geldige offerteregel in.";
    } else if (mode === "total_volume") {
      if (n(tv.total_volume_cbm) <= 0) return "Totaal volume (m³) is verplicht.";
      if (n(tv.price_per_cbm) <= 0) return "Tarief per m³ is verplicht.";
    } else if (mode === "fixed") {
      if (!(fixed.fixed_description || "").trim()) return "Vul de omschrijving in voor de vaste prijs.";
      if (n(fixed.fixed_amount) <= 0) return "Bedrag is verplicht voor vaste prijs.";
    }
    return null;
  };

  const buildPayload = () => {
    // Phase 20E — pickup_address (legacy string) auto-vullen uit NAW voor backwards-compat.
    const legacyAddr = [
      [customer.street, customer.house_number, customer.house_number_addition].filter(Boolean).join(" "),
      [customer.postal_code, customer.city].filter(Boolean).join(" "),
      customer.country,
    ].filter(Boolean).join(", ") || null;
    const base = {
      first_name: customer.first_name,
      last_name: customer.last_name,
      company_name: customer.company_name || null,
      email: customer.email,
      phone: customer.phone || null,
      whatsapp: customer.whatsapp || null,
      // NAW (Phase 20E)
      street: customer.street || null,
      house_number: customer.house_number || null,
      house_number_addition: customer.house_number_addition || null,
      postal_code: customer.postal_code || null,
      city: customer.city || null,
      country: customer.country || null,
      pickup_address: legacyAddr,
      destination_country: customer.destination_country || "Curaçao",
      shipment: {
        shipment_type: customer.shipment_type,
        method: customer.shipment_type,
      },
      internal_note: customer.internal_note || null,
      status: "nieuw",
      quote_basis: mode,
    };
    if (mode === "lines") {
      base.line_items = (lines || [])
        .filter((l) => (l.description || "").trim() || n(l.rate) > 0)
        .map((l) => ({
          description: l.description || null,
          quantity: n(l.quantity) || 1,
          length_cm: n(l.length_cm) || null,
          width_cm: n(l.width_cm) || null,
          height_cm: n(l.height_cm) || null,
          cbm: n(l.cbm) || null,
          weight_kg: n(l.weight_kg) || null,
          rate: n(l.rate),
          note: l.note || null,
          line_type: l.line_type || "cbm",
          preset_key: l.preset_key || null,
        }));
      base.pricing = {
        admin_fee: n(tv.admin_fee) || null,
        documentation_fee: n(tv.documentation_fee) || null,
        pickup_fee: n(tv.pickup_fee) || null,
        extra_handling: n(tv.extra_handling) || null,
        discount: n(tv.discount) || null,
        vat_percent: vatOrDefault(tv.vat_percent),
      };
      base.customer_note = customer.internal_note ? null : null;
    } else if (mode === "total_volume") {
      base.total_volume_cbm = n(tv.total_volume_cbm);
      base.pricing = {
        price_per_cbm: n(tv.price_per_cbm),
        admin_fee: n(tv.admin_fee) || null,
        documentation_fee: n(tv.documentation_fee) || null,
        pickup_fee: n(tv.pickup_fee) || null,
        extra_handling: n(tv.extra_handling) || null,
        discount: n(tv.discount) || null,
        vat_percent: vatOrDefault(tv.vat_percent),
      };
      if (n(tv.colli_count) > 0) {
        base.shipment.boxes = n(tv.colli_count);
      }
    } else if (mode === "fixed") {
      base.fixed_amount = n(fixed.fixed_amount);
      base.fixed_description = fixed.fixed_description;
      base.pricing = {
        admin_fee: n(fixed.admin_fee) || null,
        extra_handling: n(fixed.extra_handling) || null,
        discount: n(fixed.discount) || null,
        vat_percent: vatOrDefault(fixed.vat_percent),
      };
    }
    return base;
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/quotes-v2", buildPayload());
      toast.success("Offerte aangemaakt als concept.");
      reset();
      onCreated?.(r.data?.id);
      onClose?.();
    } catch (err2) {
      toast.error(formatApiError(err2));
    } finally {
      setBusy(false);
    }
  };

  // Per-mode pricing setters
  const setPricing_tv = (k, v) => setTv((f) => ({ ...f, [k]: v }));
  const setPricing_fixed = (k, v) => setFixed((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose?.(); } }}>
      <DialogContent className="rounded-none max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="new-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-xl">Nieuwe offerte maken</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-5">
          {/* Mode toggle */}
          <section>
            <h3 className="font-chivo font-black text-sm text-[#0055FF] uppercase tracking-wide mb-2">
              Offertebasis
            </h3>
            <ModeToggle mode={mode} onChange={handleModeChange} />
            <ModeSwitchWarning show={!!pendingMode} onConfirm={confirmModeChange} onCancel={cancelModeChange} />
          </section>

          {/* Customer */}
          <section>
            <h3 className="font-chivo font-black text-sm text-[#0055FF] uppercase tracking-wide mb-2">
              Klant & bestemming
            </h3>
            <CustomerFields form={customer} setF={setCustField} />
          </section>

          {/* Mode-specific scope */}
          <section data-testid={`quote-mode-section-${mode}`}>
            <h3 className="font-chivo font-black text-sm text-[#0055FF] uppercase tracking-wide mb-2">
              {mode === "lines" ? "Offerteregels" : mode === "total_volume" ? "Volume & tarief" : "Vaste prijs"}
            </h3>
            {mode === "total_volume" ? <TotalVolumeMode form={tv} setF={setTvField} /> : null}
            {mode === "fixed" ? <FixedMode form={fixed} setF={setFixedField} /> : null}
            {mode === "lines" ? (
              <QuoteLineItemsEditor lines={lines} onChange={setLines} presets={presets} />
            ) : null}
          </section>

          {/* Pricing extras */}
          <section>
            <h3 className="font-chivo font-black text-sm text-[#0055FF] uppercase tracking-wide mb-2">
              Extra kosten & BTW
            </h3>
            {mode === "fixed" ? (
              <PricingExtrasGrid pricing={fixed} setP={setPricing_fixed} showDocFee={false} showPickup={false} />
            ) : (
              <PricingExtrasGrid pricing={tv} setP={setPricing_tv} />
            )}
            <div className="mt-3">
              <TotalsPanel totals={liveTotals} />
            </div>
          </section>

          {/* Internal note */}
          <section>
            <Label className="text-[10px] uppercase">Interne opmerking (niet zichtbaar voor klant)</Label>
            <Textarea data-testid="nq-internal-note" value={customer.internal_note}
              onChange={(e) => setCustField("internal_note", e.target.value)}
              className="rounded-none mt-1 min-h-[50px]" />
          </section>

          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={() => { reset(); onClose?.(); }} data-testid="nq-cancel">
              Annuleren
            </Button>
            <Button type="submit" disabled={busy} className="rounded-none bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="nq-create-submit">
              <Plus className="w-4 h-4 mr-1" /> {busy ? "Aanmaken..." : "Aanmaken als concept"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
